import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthProvider } from '@/contexts/AuthContext';
import { PatientGroupsProvider } from '@/contexts/PatientGroupsContext';

// Eager — auth-critical / lightweight
import Home from '@/pages/Home';
import Login from '@/pages/Login';

// Lazy — code-split everything else
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const FirstLoginSetup = lazy(() => import('@/components/auth/FirstLoginSetup'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Calendar = lazy(() => import('@/pages/Calendar'));
const Visits = lazy(() => import('@/pages/Visits'));
const Examinations = lazy(() => import('@/pages/Examinations'));
const ExaminationForm = lazy(() => import('@/pages/ExaminationForm'));
const Certificates = lazy(() => import('@/pages/Certificates'));
const Patients = lazy(() => import('@/pages/Patients'));
const Users = lazy(() => import('@/pages/Users'));
const Statistics = lazy(() => import('@/pages/Statistics'));
const AppShell = lazy(() => import('@/layouts/AppShell'));
const SettingsShell = lazy(() => import('@/layouts/SettingsShell'));
const Settings = lazy(() => import('@/pages/Settings'));
const AccountSettings = lazy(() => import('@/pages/settings/AccountSettings'));
const AccountProfilePage = lazy(() => import('@/pages/settings/account/ProfilePage'));
const AccountDoctorPage = lazy(() => import('@/pages/settings/account/DoctorPage'));
const AccountSecurityPage = lazy(() => import('@/pages/settings/account/SecurityPage'));
const OrganizationSettingsPage = lazy(() => import('@/pages/settings/OrganizationSettings'));
const OrgCompanyPage = lazy(() => import('@/pages/settings/organization/CompanyPage'));
const OrgPreferencesPage = lazy(() => import('@/pages/settings/organization/PreferencesPage'));
const OrgSchedulePage = lazy(() => import('@/pages/settings/organization/SchedulePage'));
const ClinicalSettings = lazy(() => import('@/pages/settings/ClinicalSettings'));
const ClinicalServicesPage = lazy(() => import('@/pages/settings/clinical/ServicesPage'));
const ClinicalGroupsPage = lazy(() => import('@/pages/settings/clinical/GroupsPage'));
const ClinicalEwusPage = lazy(() => import('@/pages/settings/clinical/EwusPage'));
const BillingSettings = lazy(() => import('@/pages/settings/BillingSettings'));
const BillingSubscriptionPage = lazy(() => import('@/pages/settings/billing/SubscriptionPage'));
const BillingTerminalPage = lazy(() => import('@/pages/settings/billing/TerminalPage'));
const BillingInvoicesPage = lazy(() => import('@/pages/settings/billing/InvoicesPage'));
const FiscalSettings = lazy(() => import('@/pages/settings/FiscalSettings'));
const FiscalPrinterPage = lazy(() => import('@/pages/settings/fiscal/PrinterPage'));
const FiscalItemsPage = lazy(() => import('@/pages/settings/fiscal/ItemsPage'));
const FiscalWebhookPage = lazy(() => import('@/pages/settings/fiscal/WebhookPage'));
const FiscalFailuresPage = lazy(() => import('@/pages/settings/fiscal/FailuresPage'));
const FiscalVoidRegisterPage = lazy(() => import('@/pages/settings/fiscal/VoidRegisterPage'));
const IntegrationsSettings = lazy(() => import('@/pages/settings/IntegrationsSettings'));
const PublicBookingPage = lazy(() => import('@/pages/settings/integrations/PublicBookingPage'));
const GoogleCalendarPage = lazy(() => import('@/pages/settings/integrations/GoogleCalendarPage'));
const GoogleChatPage = lazy(() => import('@/pages/settings/integrations/GoogleChatPage'));
const SmsPage = lazy(() => import('@/pages/settings/integrations/SmsPage'));
const ApiPage = lazy(() => import('@/pages/settings/integrations/ApiPage'));
const Payments = lazy(() => import('@/pages/Payments'));
const PatientIntake = lazy(() => import('@/pages/PatientIntake'));
const IntakeManagement = lazy(() => import('@/pages/IntakeManagement'));
const PatientCard = lazy(() => import('@/pages/PatientCard'));

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-sm font-medium text-muted-foreground">Ładowanie…</p>
    </div>
  </div>
);

const SentryFallback = ({ error, resetError }: { error: unknown; resetError: () => void }) => {
  // If the boundary caught a stale-chunk error, auto-reload immediately.
  React.useEffect(() => {
    import('@/lib/chunkReload').then(({ isStaleChunkError, triggerChunkReload }) => {
      if (isStaleChunkError(error)) triggerChunkReload();
    });
  }, [error]);

  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-xl font-semibold text-foreground">Coś poszło nie tak</h1>
        <p className="text-sm text-muted-foreground">
          Wystąpił nieoczekiwany błąd. Zespół został o nim powiadomiony.
        </p>
        <button
          onClick={resetError}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
        >
          Spróbuj ponownie
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <Router>
        <AuthProvider>
          <PatientGroupsProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/intake/:token" element={<PatientIntake />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Initial setup after login */}
                <Route
                  path="/setup"
                  element={
                    <ProtectedRoute>
                      <FirstLoginSetup />
                    </ProtectedRoute>
                  }
                />

                {/* All authenticated routes share a single AppShell so the
                  sidebar / topbar mount only once and navigations swap just
                  the inner <Outlet />, not the whole chrome. */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppShell />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/visits" element={<Visits />} />
                  <Route path="/examinations" element={<Examinations />} />
                  <Route path="/examinations/:appointmentId" element={<ExaminationForm />} />
                  <Route path="/certificates" element={<Certificates />} />
                  <Route path="/patients" element={<Patients />} />
                  <Route path="/patients/:id" element={<PatientCard />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/statistics" element={<Statistics />} />
                  <Route path="/payments" element={<Payments />} />
                  <Route path="/intake-management" element={<IntakeManagement />} />

                  {/* Settings: nested shell keeps sidebar + command palette
                    mounted; only the section panel re-renders. */}
                  <Route path="/settings" element={<SettingsShell />}>
                    <Route index element={<Settings />} />

                    <Route path="account" element={<AccountSettings />} />
                    <Route path="account/profile" element={<AccountProfilePage />} />
                    <Route path="account/doctor" element={<AccountDoctorPage />} />
                    <Route path="account/security" element={<AccountSecurityPage />} />

                    <Route path="organization" element={<OrganizationSettingsPage />} />
                    <Route path="organization/company" element={<OrgCompanyPage />} />
                    <Route path="organization/preferences" element={<OrgPreferencesPage />} />
                    <Route path="organization/schedule" element={<OrgSchedulePage />} />

                    <Route path="clinical" element={<ClinicalSettings />} />
                    <Route path="clinical/services" element={<ClinicalServicesPage />} />
                    <Route path="clinical/groups" element={<ClinicalGroupsPage />} />
                    <Route path="clinical/ewus" element={<ClinicalEwusPage />} />

                    <Route path="billing" element={<BillingSettings />} />
                    <Route path="billing/subscription" element={<BillingSubscriptionPage />} />
                    <Route path="billing/terminal" element={<BillingTerminalPage />} />
                    <Route path="billing/invoices" element={<BillingInvoicesPage />} />

                    <Route path="fiscal" element={<FiscalSettings />} />
                    <Route path="fiscal/printer" element={<FiscalPrinterPage />} />
                    <Route path="fiscal/items" element={<FiscalItemsPage />} />
                    <Route path="fiscal/webhook" element={<FiscalWebhookPage />} />
                    <Route path="fiscal/failures" element={<FiscalFailuresPage />} />
                    <Route path="fiscal/void-register" element={<FiscalVoidRegisterPage />} />

                    <Route path="integrations" element={<IntegrationsSettings />} />
                    <Route path="integrations/public-booking" element={<PublicBookingPage />} />
                    <Route path="integrations/google-calendar" element={<GoogleCalendarPage />} />
                    <Route path="integrations/google-chat" element={<GoogleChatPage />} />
                    <Route path="integrations/sms" element={<SmsPage />} />
                    <Route path="integrations/api" element={<ApiPage />} />
                  </Route>
                </Route>

                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </PatientGroupsProvider>
        </AuthProvider>
      </Router>
    </Sentry.ErrorBoundary>
  );
}

export default App;
